/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
/**
 * useCreateTransmittalForm
 *
 * Platform-agnostic hook for managing create transmittal form state.
 * Handles form state, validation, space loading, and submission reviewFlow.
 *
 * Usage:
 * - Web: Used in CreateTransmittal.tsx modal
 * - Mobile: Used in create transmittal screen
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import {
  Space,
  OriginSpace,
  SelectedUser,
  TransmittalReference,
  ReferenceDatasetItem,
  TransmittalPayload,
  FormTemplate,
  SpaceUserPayload,
} from "./transmittal.types";
import useTransmittalReferences from "./useTransmittalReferences";
import useSpaceUserSelection from "./useSpaceUserSelection";
import { getFormTemplate } from "@Actions/fileUpload";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from "@Services/constants";
import { t, untranslated } from "@i18n";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

interface UseCreateTransmittalFormParams {
  projectId: string;
  selectedSpaceId?: string | null;
  selectedReferences?: TransmittalReference[] | null;
  /** Sent as `saveTransmittalsRequest.reviewData.reviewId`, so it carries that field's brand. */
  reviewId?: NonNullable<TransmittalPayload["reviewData"]>["reviewId"] | null;
  isOpen?: boolean;
  mobile?: boolean;

  // API adapters
  api: {
    fetchSpaces: (projectId: string) => Promise<Space[]>;
    fetchFormTemplate?: (projectId: string, resourceType: string) => Promise<FormTemplate | null>;
    saveTransmittal: (
      data: TransmittalPayload,
    ) => Promise<{ data: { status: boolean; data?: any; errorMessage?: string } }>;
  };

  // Callbacks
  callbacks?: {
    onSuccess?: (response: any) => void;
    onError?: (error: string) => void;
    onClose?: () => void;
  };

  // Form validation (for custom fields)
  validateCustomFields?: () => boolean;
}

interface ValidationError {
  field: string;
  message: string;
}

interface UseCreateTransmittalFormReturn {
  // Loading states
  isLoading: boolean;
  isSubmitting: boolean;

  // Space state
  originSpace: OriginSpace | null;
  spaces: Space[];
  filteredSpaceList: Space[];
  enableOriginSpace: boolean;

  // Form fields
  transmittalSubject: string;
  transmittalRemarks: string;
  formTemplate: FormTemplate | null;

  // User selection (from sub-hook)
  selectedToUsers: SelectedUser[];
  selectedNotifyUsers: SelectedUser[];
  toUsersBySpace: Record<string, SelectedUser[]>;
  notifyUsersBySpace: Record<string, SelectedUser[]>;
  hasToUsers: boolean;
  hasNotifyUsers: boolean;
  getToSpaceUserPayload: () => SpaceUserPayload[];
  getNotifySpaceUserPayload: () => SpaceUserPayload[];

  // References (from sub-hook)
  references: Record<string, TransmittalReference>;
  referencesDataset: ReferenceDatasetItem[];
  alreadyAttachedIds: string[];
  hasReferences: boolean;

  // Validation
  canSubmit: boolean;
  validationErrors: ValidationError[];
  hasMinimumSpaces: boolean;

  // Enable states for buttons
  isToSpaceEnabled: boolean;
  isNotifySpaceEnabled: boolean;

  // Actions - Form fields
  setOriginSpace: (space: OriginSpace | null) => void;
  setSubject: (subject: string) => void;
  setRemarks: (remarks: string) => void;
  handleFormData: (template: any) => void;

  // Actions - Users
  addToUsers: (users: SelectedUser[]) => void;
  removeToUser: (userId: string) => void;
  setToUsers: (users: SelectedUser[]) => void;
  addNotifyUsers: (users: SelectedUser[]) => void;
  removeNotifyUser: (userId: string) => void;
  setNotifyUsers: (users: SelectedUser[]) => void;

  // Actions - References
  addReferences: (refs: TransmittalReference[]) => void;
  removeReferences: (ids: string[]) => void;
  updateReferenceRemark: (id: string, remark: string) => void;

  // Actions - Form
  validate: () => boolean;
  submit: () => Promise<{ success: boolean; data?: any; error?: string }>;
  reset: () => void;

  // Utilities
  changeOriginSpace: (spaceId: string, spaceData?: any) => void;
}

function useCreateTransmittalForm({
  projectId,
  selectedSpaceId: _selectedSpaceId = null,
  selectedReferences = null,
  reviewId = null,
  isOpen: _isOpen = true,
  mobile: _mobile = false,
  api,
  callbacks,
  validateCustomFields,
}: UseCreateTransmittalFormParams): UseCreateTransmittalFormReturn {
  // ============================================================================
  // Redux
  // ============================================================================
  const dispatch = useDispatch();

  // ============================================================================
  // State
  // ============================================================================

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [originSpace, setOriginSpaceState] = useState<OriginSpace | null>(null);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [allProjectSpaces, setAllProjectSpaces] = useState<Space[]>([]);
  const [enableOriginSpace] = useState<boolean>(true);

  const [transmittalSubject, setTransmittalSubject] = useState<string>("");
  const [transmittalRemarks, setTransmittalRemarks] = useState<string>("");
  const [formTemplate, setFormTemplate] = useState<FormTemplate | null>(null);

  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // Sub-hooks for user selection
  const toUsersHook = useSpaceUserSelection({ initialUsers: [] });
  const notifyUsersHook = useSpaceUserSelection({ initialUsers: [] });

  // Sub-hook for references
  const referencesHook = useTransmittalReferences({
    initialReferences: selectedReferences || [],
  });

  // ============================================================================
  // Derived State
  // ============================================================================

  // Filter all project spaces down to those eligible as "To" destinations (any space except origin)
  const filteredSpaceList = useMemo<Space[]>(() => {
    if (!originSpace?._id) return [];
    return allProjectSpaces.filter((space) => space._id !== originSpace._id);
  }, [allProjectSpaces, originSpace?._id]);

  // Check if project has minimum required spaces (uses total project count, not just user's spaces)
  const hasMinimumSpaces = useMemo(() => allProjectSpaces.length >= 2, [allProjectSpaces.length]);

  // Enable states for To/Notify buttons (based on origin space selection)
  const isToSpaceEnabled = Boolean(originSpace?._id);
  const isNotifySpaceEnabled = Boolean(originSpace?._id);

  // Validation: Can submit form
  const canSubmit = useMemo(() => {
    return (
      transmittalSubject.trim().length > 0 &&
      referencesHook.hasReferences &&
      toUsersHook.hasUsers &&
      hasMinimumSpaces &&
      !isSubmitting
    );
  }, [transmittalSubject, referencesHook.hasReferences, toUsersHook.hasUsers, hasMinimumSpaces, isSubmitting]);

  // ============================================================================
  // Effects
  // ============================================================================

  // Load ALL project spaces (not filtered by user membership) for minimum-spaces check and To selector
  useEffect(() => {
    const loadAllProjectSpaces = async () => {
      if (!projectId) return;
      try {
        const result = await api.fetchSpaces(projectId);
        setAllProjectSpaces(result);
      } catch (error) {
        console.error("Error loading project spaces:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    };
    loadAllProjectSpaces();
  }, [projectId]);

  // Load form template
  useEffect(() => {
    const loadFormTemplate = async () => {
      if (!projectId || !api.fetchFormTemplate) return;

      try {
        const response = await getFormTemplate({
          projectId,
          resourceType: "TRANSMITTAL",
        });
        if (response.data.status && response.data.data) {
          setFormTemplate(response.data.data as FormTemplate);
        }
      } catch (error) {
        console.error("Error loading form template:", error);
        Promise.reject(error);
      }
    };

    loadFormTemplate();
  }, [projectId]);

  // ============================================================================
  // Actions
  // ============================================================================

  // Set origin space
  const setOriginSpace = useCallback((space: OriginSpace | null) => {
    setOriginSpaceState(space);
  }, []);

  // Change origin space (clears form data - users and references, then restores initial references)
  const changeOriginSpace = useCallback(
    (spaceId: string, spaceData?: Space[]) => {
      reset();
      if (selectedReferences?.length) {
        referencesHook.addReferences(selectedReferences);
      }
      if (spaceData) {
        setSpaces(spaceData);
      }
      setIsLoading(false);
      const selectedSpace = spaceData?.find((space) => space._id === spaceId);
      if (selectedSpace) {
        setOriginSpaceState({
          _id: selectedSpace._id,
          spaceName: selectedSpace.spaceName,
        });
      }
    },

    [spaces, toUsersHook, notifyUsersHook, referencesHook, selectedReferences],
  );

  // Set subject
  const setSubject = useCallback((subject: string) => {
    setTransmittalSubject(subject);
  }, []);

  // Set remarks
  const setRemarks = useCallback((remarks: string) => {
    setTransmittalRemarks(remarks);
  }, []);

  // Update form template
  const handleFormData = useCallback((data: any) => {
    setFormTemplate((prev) => (prev ? { ...prev, template: data } : null));
  }, []);

  // Validate form
  const validate = useCallback((): boolean => {
    const errors: ValidationError[] = [];

    if (!transmittalSubject.trim()) {
      errors.push({ field: "subject", message: "Subject is required" });
    }

    if (!referencesHook.hasReferences) {
      errors.push({ field: "references", message: "At least one reference is required" });
    }

    if (!toUsersHook.hasUsers) {
      errors.push({ field: "toUsers", message: "At least one To user is required" });
    }

    if (!hasMinimumSpaces) {
      errors.push({ field: "spaces", message: "Minimum 2 spaces required in project" });
    }

    setValidationErrors(errors);
    return errors.length === 0;
  }, [transmittalSubject, referencesHook.hasReferences, toUsersHook.hasUsers, hasMinimumSpaces]);

  // Submit form
  const submit = useCallback(async (): Promise<{ success: boolean; data?: any; error?: string }> => {
    // Validate custom fields first (if validator provided)
    if (validateCustomFields && !validateCustomFields()) {
      dispatch(showToastNotification({ content: t("Please check custom fields"), type: TOAST_TYPE_ERROR }));
      return { success: false, error: "Custom fields validation failed" };
    }

    if (!validate()) {
      // Return specific error message based on what failed
      if (!toUsersHook.hasUsers) {
        dispatch(showToastNotification({ content: t("Please select at least one To user"), type: TOAST_TYPE_ERROR }));
        return { success: false, error: "No To users selected" };
      }
      if (!transmittalSubject.trim()) {
        dispatch(showToastNotification({ content: t("Please enter a subject"), type: TOAST_TYPE_ERROR }));
        return { success: false, error: "Subject is required" };
      }
      if (!referencesHook.hasReferences) {
        dispatch(showToastNotification({ content: t("Please add at least one reference"), type: TOAST_TYPE_ERROR }));
        return { success: false, error: "No references added" };
      }
      dispatch(showToastNotification({ content: t("Please check form validation errors"), type: TOAST_TYPE_ERROR }));
      return { success: false, error: "Validation failed" };
    }

    // `validate()` does not cover the origin Space, so guard it here: `spaceId` is a required
    // field of the saveTransmittalsData request and must never be sent as an empty string.
    if (!originSpace?._id) {
      dispatch(showToastNotification({ content: t("Please select origin space"), type: TOAST_TYPE_ERROR }));
      return { success: false, error: "Origin space is required" };
    }

    setIsSubmitting(true);

    try {
      // Entry point: `OriginSpace._id` is a plain `string` — it is built from the platform-specific
      // `api.fetchSpaces()` result (web QueryBuilder / mobile fetch), not from a branded schema —
      // so it is asserted once here at its declaration (presence is guaranteed by the guard above).
      const spaceId = originSpace._id as ObjectIdString;
      const payload: TransmittalPayload = {
        subject: transmittalSubject.trim(),
        remarks: transmittalRemarks,
        spaceId,
        toSpaceIds: toUsersHook.toSpaceUserPayload(),
        ccSpaceIds: notifyUsersHook.toSpaceUserPayload(),
        attachedResources: referencesHook.getAttachedResourcesPayload(),
        forwardedResources: referencesHook.getForwardedResourcesPayload(),
      };

      if (formTemplate) {
        payload.formTemplate = {
          _id: formTemplate._id,
          template: formTemplate.template,
        };
      }

      if (reviewId) {
        payload.reviewData = { reviewId };
      }

      const response = await api.saveTransmittal(payload);

      if (response.data.status) {
        const counter = response.data.data?.counter;
        dispatch(
          showToastNotification({
            content: counter
              ? t("TS-{{counter}} has been sent successfully!", { counter })
              : t("Transmittal sent successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        callbacks?.onSuccess?.(response.data.data);
        return { success: true, data: response.data.data };
      } else {
        const errorMessage = untranslated(response.data.errorMessage || "") || t("Failed to create transmittal");
        dispatch(showToastNotification({ content: errorMessage, type: TOAST_TYPE_ERROR }));
        callbacks?.onError?.(String(errorMessage));
        return { success: false, error: String(errorMessage) };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? untranslated(error.message) : t("An error occurred");
      dispatch(showToastNotification({ content: errorMessage, type: TOAST_TYPE_ERROR }));
      callbacks?.onError?.(String(errorMessage));
      Promise.reject(error);
      return { success: false, error: String(errorMessage) };
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validate,
    validateCustomFields,
    transmittalSubject,
    transmittalRemarks,
    originSpace,
    toUsersHook,
    notifyUsersHook,
    referencesHook,
    formTemplate,
    reviewId,
    api,
    callbacks,
    dispatch,
  ]);

  // Reset form
  const reset = useCallback(() => {
    setTransmittalSubject("");
    setTransmittalRemarks("");
    toUsersHook.clear();
    notifyUsersHook.clear();
    referencesHook.clear();
    setValidationErrors([]);
  }, [toUsersHook, notifyUsersHook, referencesHook]);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // Loading states
    isLoading,
    isSubmitting,

    // Space state
    originSpace,
    spaces,
    filteredSpaceList,
    enableOriginSpace,

    // Form fields
    transmittalSubject,
    transmittalRemarks,
    formTemplate,

    // User selection
    selectedToUsers: toUsersHook.selectedUsers,
    selectedNotifyUsers: notifyUsersHook.selectedUsers,
    toUsersBySpace: toUsersHook.usersBySpace,
    notifyUsersBySpace: notifyUsersHook.usersBySpace,
    hasToUsers: toUsersHook.hasUsers,
    hasNotifyUsers: notifyUsersHook.hasUsers,
    getToSpaceUserPayload: toUsersHook.toSpaceUserPayload,
    getNotifySpaceUserPayload: notifyUsersHook.toSpaceUserPayload,

    // References
    references: referencesHook.references,
    referencesDataset: referencesHook.referencesDataset,
    alreadyAttachedIds: referencesHook.alreadyAttachedIds,
    hasReferences: referencesHook.hasReferences,

    // Validation
    canSubmit,
    validationErrors,
    hasMinimumSpaces,

    // Enable states for buttons
    isToSpaceEnabled,
    isNotifySpaceEnabled,

    // Actions - Form fields
    setOriginSpace,
    setSubject,
    setRemarks,
    handleFormData,

    // Actions - Users
    addToUsers: toUsersHook.addUsers,
    removeToUser: toUsersHook.removeUser,
    setToUsers: toUsersHook.setUsers,
    addNotifyUsers: notifyUsersHook.addUsers,
    removeNotifyUser: notifyUsersHook.removeUser,
    setNotifyUsers: notifyUsersHook.setUsers,

    // Actions - References
    addReferences: referencesHook.addReferences,
    removeReferences: referencesHook.removeReferences,
    updateReferenceRemark: referencesHook.updateRemark,

    // Actions - Form
    validate,
    submit,
    reset,

    // Utilities
    changeOriginSpace,
  };
}

export default useCreateTransmittalForm;
